import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    Field,
    ConstitutiveActServiceRequest,
    EfeResponse,
    UploadDocumentServiceRequest,
    Documents,
} from 'commons/models';
import {EfeAccordion} from 'components';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {MainContent} from 'layouts/commons';
import {formatDateToISO, getBase64Extension, nextStep} from 'commons/utils/Functions';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import DocsActForm from './docs-act-form/DocsActForm';
import {ClientService, DocumentServices} from 'services';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';

function DocsAct() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {menu, expedientId, fatherKycId} = useMenuContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [constituviveAct, setConstitutiveAct] = React.useState<any>(undefined);

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.DATA_DOCUMENTATION_CONST_ACT === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const response: EfeResponse<ConstitutiveActServiceRequest> =
                        await ClientService.getConstitutiveAct(fatherKycId);
                    if (response && response.data) {
                        try {
                            const act: EfeResponse<Documents> =
                                await DocumentServices.getDocumentByKycId(
                                    Constants.DOC_ACTA_CONSTITUTIVA,
                                    expedientId,
                                    fatherKycId
                                );
                            if (act && act.data) {
                                response.data.file = act.data.data;
                            }
                        } catch (error) {
                            response.data.file = '';
                        }
                        setConstitutiveAct(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setConstitutiveAct(undefined);
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Acta Constitutiva',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <DocsActForm
                            fields={fields}
                            submitHandler={onSubmit}
                            constituviveAct={constituviveAct}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.DATA_DOCUMENTATION_CONST_ACT === flow.data.expandKey,
        },
    ];

    async function onSubmit(values: ConstitutiveActServiceRequest) {
        setIsLoading(true);
        try {
            const request = {...values};
            request.viewFatherId = flow.data.fatherId;
            request.viewId = flow.data.viewId;
            request.expedientId = expedientId;
            request.kycId = fatherKycId;
            request.workflowId = 1;
            request.file = '';
            request.protocolizationDate = formatDateToISO(values.protocolizationDate);
            request.constitutionDate = values.protocolizationDate;
            const response: EfeResponse<ConstitutiveActServiceRequest> =
                await ClientService.updateConstitutiveAct(request);
            if (response && response.data) {
                const extension = getBase64Extension(values.file);
                const fileConverted = await (await fetch(values.file)).blob();
                const document: UploadDocumentServiceRequest = {
                    documentKey: Constants.DOC_ACTA_CONSTITUTIVA,
                    expedientId: expedientId,
                    file: fileConverted,
                    kycId: fatherKycId,
                    filename: `${Constants.DOC_ACTA_CONSTITUTIVA}.${extension}`,
                };

                const documentResponse: EfeResponse<any> = await DocumentServices.updateDocument(
                    document
                );

                if (documentResponse && documentResponse.data) {
                    enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                    const path = nextStep(flow, menu);
                    if (Constants.MODULE_STEP !== path) {
                        setIsLoading(false);
                        history.push(path);
                    } else {
                        updateModuleStep(true);
                        flow.data.expandKey = path;
                        setIsLoading(false);
                    }
                } else {
                    setFields([...fields]);
                    setIsLoading(false);
                }
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.DOCS_ACT)}
                values={accordion}
            />
        </MainContent>
    );
}

export default DocsAct;
